import { map as rxjsmap } from 'rxjs';

import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { BaseServices } from './../../kuba.services';
import { forkJoin } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaderResponse } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import {
  BusinessAgreement,
  BusinessEdit,
  BusinessImportConfirmRequest,
  BusinessImportConfirmResponse,
  BusinessImportPreviewResponse,
  BusinessList
} from '../models';

import { shareReplay, tap, map } from 'rxjs/operators';
import { EmployeeLicenseUpgradeQuote, EmployeeLicenseUsage } from '../models/employee-license';

@Injectable()
export class BusinessServices {
  _business: Observable<any> = null!;

  constructor(private http: HttpClient) {}

  clearCachedBusinessService() {
    this._business = null!;
  }
  // List all business (using pagination with large page size)

  list(): Observable<BusinessList[]> {
    this.clearCachedBusinessService();

    if (!this._business) {
      // Use the old GET endpoint to get all businesses
      this._business = this.http
        .get<any>(environment.BASE_URL + '/businesses')
        .pipe(
          map(result => result || []),
          tap(result => {}),
          shareReplay(1)
        );
    }
    return this._business;
  }

  // Get paginated business list
  getPagedList(filter: any): Observable<any> {
    return this.http
      .post<any>(environment.BASE_URL + '/businesses/paged', filter).pipe(rxjsmap(result => result));
  }

  // List all business
  activeBusinesslist() {
    this.clearCachedBusinessService();
    if (!this._business) {
      return this.http
        .get(environment.BASE_URL + '/activebusinesses').pipe(rxjsmap(result => result))
        .pipe(shareReplay(1));
    }
    return this._business;
  }

  // Get business names for dropdown binding (uses faster /businesses/names endpoint)
  getBusinessNames() {
    return this.http
      .get(environment.BASE_URL + '/businesses/names').pipe(rxjsmap(result => result));
  }
  // retrive a business for editing and View
  getById(id: number) {
    return this.http
      .get<any>(environment.BASE_URL + '/business/' + id).pipe(rxjsmap(result => result));
  }

  getBusinessForFeature(id: number) {
    return this.http
      .get(environment.BASE_URL + '/business/' + id).pipe(rxjsmap((result: any) => {
        if (result) {
          let data = result;
          let featureBase = BaseServices.getUserFeatures();
          featureBase.Features = data.Features;
          featureBase.Type = 'business';
          featureBase.Details = result;
          let val: any = null;
          return val;
        }
      }));
  }
  create(newBusiness: BusinessEdit) {
    return this.http
      .post(environment.BASE_URL + '/business', newBusiness).pipe(rxjsmap(result => result));
  }

  previewImport(file: File): Observable<BusinessImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<BusinessImportPreviewResponse>(
      `${environment.BASE_URL_V2}/businesses/import/preview`,
      formData,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  confirmImport(file: File, request: BusinessImportConfirmRequest, jobId: string): Observable<BusinessImportConfirmResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('rowsJson', JSON.stringify(request));
    formData.append('jobId', jobId);

    return this.http.post<BusinessImportConfirmResponse>(
      `${environment.BASE_URL_V2}/businesses/import/confirm`,
      formData,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  getImport(jobId: string, offset = 0, limit = 0, filter = 'all'): Observable<BusinessImportConfirmResponse> {
    return this.http.get<BusinessImportConfirmResponse>(
      `${environment.BASE_URL_V2}/businesses/import/jobs/${jobId}`,
      { params: { offset, limit, filter }, context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  recentImports(): Observable<BusinessImportConfirmResponse[]> {
    return this.http.get<BusinessImportConfirmResponse[]>(
      `${environment.BASE_URL_V2}/businesses/import/jobs`,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  importReport(jobId: string): Observable<Blob> {
    return this.http.get(`${environment.BASE_URL_V2}/businesses/import/jobs/${jobId}/report`,
      { responseType: 'blob', context: new HttpContext().set(SKIP_ERROR_TOAST, true) });
  }

  retryImport(jobId: string): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL_V2}/businesses/import/jobs/${jobId}/retry`, {},
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) });
  }

  update(id: number, ExistingBusiness: BusinessEdit) {
    return this.http
      .put(environment.BASE_URL + '/business/' + id, ExistingBusiness).pipe(rxjsmap(result => result));
  }

  deleteById(id: number, newBusiness: BusinessEdit) {
    return this.http
      .put<boolean>(`${environment.BASE_URL}/businessDelete/${id}`, newBusiness).pipe(rxjsmap(result => result));
  }

  // retrive a agreement for a business
  getAgreementByBusinessId(businessId: number) {
    return this.http
      .get<any>(environment.BASE_URL + '/licences/' + businessId).pipe(rxjsmap(result => result));
  }

  /**
   * Gets a business' parent portal id
   * @param businessId Id of business to fetch from
   * @returns Portal Id
   */
  getBusinessPortalId(businessId: number) {
    return this.http
      .get<number>(`${environment.BASE_URL}/business/${businessId}/portal`).pipe(rxjsmap(result => result));
  }

  getUsersByBusiness(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/userbybusiness/' + businessId).pipe(rxjsmap((result: any) => result));
  }

  add(newBusinessAgreement: BusinessAgreement) {
    return this.http
      .post(environment.BASE_URL + '/licenceadd', newBusinessAgreement).pipe(rxjsmap(result => result));
  }
  updateAgreement(newBusinessAgreement: BusinessAgreement) {
    return this.http
      .put(environment.BASE_URL + '/licenceupdate', newBusinessAgreement,
        { context: new HttpContext().set(SKIP_ERROR_TOAST, true) })
      .pipe(rxjsmap(result => result));
  }

  getHseManager(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/hsemanager/' + businessId).pipe(rxjsmap((result: any) => result));
  }

  /**
   * Gets the contacts registered on a business, with and without a login.
   * Unlike getHseManager this returns contacts only, never employees.
   * @param businessId The business id
   * @returns Observable of the contacts, or false when the business has none
   */
  getContactsByBusiness(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/customercontactbybusiness/' + businessId).pipe(rxjsmap((result: any) => result));
  }

  /**
   * Reads how many employees the business' license agreement allows and how many of
   * those seats are already used.
   * @param businessId The business to report on
   * @returns Observable of the limit and the current usage
   */
  getEmployeeLimit(businessId: number) {
    return this.http.get<EmployeeLicenseUsage>(
      `${environment.BASE_URL_V2}/businesses/${businessId}/employee-limit`
    );
  }

  /**
   * Prices raising the business' employee limit. Nothing is changed by asking.
   * @param businessId The business to price the upgrade for
   * @param additionalEmployees How many employees to add to the limit
   * @returns Observable of the offer
   */
  getEmployeeLimitUpgradeQuote(businessId: number, additionalEmployees = 1) {
    return this.http.get<EmployeeLicenseUpgradeQuote>(
      `${environment.BASE_URL_V2}/businesses/${businessId}/employee-limit/upgrade-quote`,
      {
        params: { additionalEmployees },
        context: new HttpContext().set(SKIP_ERROR_TOAST, true)
      }
    );
  }

  /**
   * Applies an upgrade the customer accepted. The offer is sent back with it so a stale
   * or repeated acceptance is refused by the server rather than charged again.
   * @param businessId The business being upgraded
   * @param quote The offer being accepted
   * @returns Observable of the applied upgrade
   */
  acceptEmployeeLimitUpgrade(businessId: number, quote: EmployeeLicenseUpgradeQuote) {
    return this.http.post<EmployeeLicenseUpgradeQuote>(
      `${environment.BASE_URL_V2}/businesses/${businessId}/employee-limit/upgrade`,
      {
        expectedEmployeeLimit: quote.NewEmployeeLimit - quote.AdditionalEmployees,
        additionalEmployees: quote.AdditionalEmployees,
        expectedAmount: quote.Amount
      },
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  getPortalContact(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/portalcontact/' + businessId).pipe(rxjsmap(result => result));
  }

  getHpdOptions(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/hpdOptions/' + businessId).pipe(rxjsmap((result: any) => result));
  }

  getAllUsers() {
    return this.http.get(environment.BASE_URL + '/users').pipe(rxjsmap(result => result));
  }
  deleteUser(id: number) {
    return this.http
      .delete(environment.BASE_URL + '/adminuser/' + id).pipe(rxjsmap((response: any) => {
        return response;
      }));
  }
  // Active and Inactive the Business
  statusUpdate(id: number, status: string) {
    return this.http
      .put(
        environment.BASE_URL + '/businessUpdate/' + id + '/' + status,
        status
      ).pipe(rxjsmap(result => result));
  }
  getOrganizationNumber(number: number) {
    return this.http
      .get(environment.BASE_URL + '/checkOrgNumber/' + number).pipe(rxjsmap(result => result));
  }

  getBusinessByOrgNo(orgNumber: number) {
    return this.http
      .get(environment.BASE_URL + '/checkOrgNumber/' + orgNumber).pipe(rxjsmap(result => result));
  }
  getByUserId(id: number) {
    return this.http
      .get(environment.BASE_URL + '/user/' + id).pipe(rxjsmap(result => result));
  }
  updatePassword(userCredential: any) {
    return this.http
      .put(environment.BASE_URL + '/update-password', userCredential, {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true)
      }).pipe(rxjsmap(result => result));
  }
  updatePasswordAndSendSMS(userCredential: any, languageId: Number) {
    return this.http
      .put(
        environment.BASE_URL + '/update-password-sendsms/' + languageId,
        userCredential,
        { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
      ).pipe(rxjsmap(result => result));
  }
  addUsersAndSendSMS(userDetails: any, handleErrors = false) {
    return this.http
      .post(
        environment.BASE_URL +
          '/save-user-sendsms/' +
          BaseServices.userLanguageId,
        userDetails,
        { context: new HttpContext().set(SKIP_ERROR_TOAST, handleErrors) }
      ).pipe(rxjsmap(result => result));
  }
  addProjectUsers(projectUserListDto: any) {
    return this.http
      .post(environment.BASE_URL + '/project/project-user', projectUserListDto).pipe(rxjsmap(result => result));
  }
  addFDVUsers(fdvUserListDto: any) {
    return this.http
      .post(environment.BASE_URL + '/project/fdv-user', fdvUserListDto).pipe(rxjsmap(result => result));
  }

  /**
   * handling multiple service
   * @param fdvUserListDto
   * @param projectUserListDto
   */
  saveProjectFDVforGuest(
    fdvUserListDto: any,
    projectUserListDto: any,
    uid: number
  ): Observable<any[]> {
    let fdvuserRes = this.http.post(
      environment.BASE_URL + '/project/fdv-user/' + uid,
      fdvUserListDto
    );
    let projectuserRes = this.http.post(
      environment.BASE_URL + '/project/project-user/' + uid,
      projectUserListDto
    );
    return forkJoin([fdvuserRes, projectuserRes]);
  }

  assignKundeELUsers(kundeELUserListDto: any) {
    return this.http
      .post(environment.BASE_URL + '/kundeEL/kundeel-user', kundeELUserListDto).pipe(rxjsmap(result => result));
  }

  reportGenerate(
    businessId: number,
    type: number,
    cultureInfo: string,
    ReportJson: any,
    applicationId: number
  ) {
    let dynamicType = 'application/pdf';
    let myData = { data: ReportJson };
    let url = `${environment.BASE_URL}/export-ourcompanydetailslist/${businessId}/${type}/${cultureInfo}/${applicationId}`;
    return this.http
      .post(url, myData, { responseType: 'blob' })
      .pipe(rxjsmap(blob => new Blob([blob], { type: dynamicType })));
  }

  getBusinessCreator(businessId: number) {
    return this.http
      .get(environment.BASE_URL + '/businesscreator/' + businessId).pipe(rxjsmap((result: any) => result));
  }

  businessDepartments(BusinessId: number) {
    return this.http
      .get<HttpHeaderResponse>(
        environment.BASE_URL +
          '/businessdepartments/' +
          BusinessId +
          '/' +
          BaseServices.ApplicationId
      ).pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }
  portalBusinessDepartments(PortalId: number) {
    return this.http
      .get<HttpHeaderResponse>(
        environment.BASE_URL +
          '/portalbusinessdepartments/' +
          PortalId +
          '/' +
          BaseServices.ApplicationId
      ).pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }

  getAllLicenseCategory() {
    return this.http
      .get<HttpHeaderResponse>(environment.BASE_URL + '/licensecategory/getall').pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }
  checkclientNum(clientNum: string) {
    return this.http
      .get(
        environment.BASE_URL +
          '/electro/check-clientnum/' +
          BaseServices.BusinessId +
          '/' +
          clientNum
      ).pipe(rxjsmap(result => result));
  }

  getAllAdminBusinesses() {
    // Use pagination with large page size to get all admin businesses
    const filter = {
      PageNumber: 1,
      PageSize: 10000, // Large number to get all businesses
      SortColumn: 'CompanyName',
      SortDirection: 'ASC'
    };

    return this.http
      .post<any>(environment.BASE_URL + '/businesses/paged', filter)
      .pipe(
        map((response: any) => {
          return response.Items || [];
        })
      );
  }
  getAllPortalBusiness(portalId: number, userId: number) {
    return this.http
      .get<HttpHeaderResponse>(
        environment.BASE_URL + '/assignedbusiness/' + portalId + '/' + userId
      ).pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }

  getPortalRiskAnalysis(userId: number) {
    return this.http
      .get<any>(environment.BASE_URL + '/portalriskanalysis/' + userId).pipe(rxjsmap(result => result));
  }

  getUsersCount(businessId: number) {
    return this.http
      .get<HttpHeaderResponse>(
        environment.BASE_URL + '/countOfUsers' + '/' + businessId
      ).pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }

  getEmployeesCount(businessId: number) {
    return this.http
      .get<HttpHeaderResponse>(
        environment.BASE_URL + '/activeemployeesCount' + '/' + businessId
      ).pipe(rxjsmap((response: HttpHeaderResponse) => {
        return response;
      }));
  }

  LeftNavigationAssignAccess(
    businessId: number,
    appId: any,
    currentUserRole: any
  ) {
    return this.http
      .get(
        environment.BASE_URL +
          '/leftNavigationAssignAccess/' +
          businessId +
          '/' +
          appId +
          '/' +
          currentUserRole +
          '/' +
          BaseServices.UserId
      ).pipe(rxjsmap(res => res));
  }
}
